import { ref, type Ref } from 'vue'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export type ToastPosition =
	| 'top-left'
	| 'top-right'
	| 'top-center'
	| 'bottom-left'
	| 'bottom-right'
	| 'bottom-center'

export interface ToastAction {
	label: string
	onClick: () => void
}

export interface ToastLink {
	href: string
	label: string
	target?: string
}

export interface ToastItem {
	id: string
	type: ToastType
	message: string
	description?: string
	action?: ToastAction
	link?: ToastLink
	autoClose: number | false
	createdAt: number
	hardColor?: boolean
}

export interface ToastOptions {
	id?: string
	type?: ToastType
	description?: string
	action?: ToastAction
	link?: ToastLink
	autoClose?: number | false
	hardColor?: boolean
}

const DEFAULT_AUTO_CLOSE = 6000
const MAX_TOASTS = 50

const items = ref<ToastItem[]>([])
const position = ref<ToastPosition>('bottom-right')

interface TimerState {
	remainingMs: number
	startedAt: number
	timeoutId: ReturnType<typeof setTimeout> | null
}

const timers = new Map<string, TimerState>()

const generateId = () => `toast-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

const clearTimer = (id: string) => {
	const t = timers.get(id)
	if (t?.timeoutId) clearTimeout(t.timeoutId)
	timers.delete(id)
}

const scheduleRemoval = (id: string, ms: number) => {
	const handle = setTimeout(() => removeToast(id), ms)
	timers.set(id, { remainingMs: ms, startedAt: Date.now(), timeoutId: handle })
}

export const useToasts = (): Ref<ToastItem[]> => items
export const useToastPosition = (): Ref<ToastPosition> => position

export const setToastPosition = (next: ToastPosition) => {
	position.value = next
}

export const addToast = (message: string, options: ToastOptions = {}): string => {
	const id = options.id ?? generateId()
	const auto = options.autoClose ?? DEFAULT_AUTO_CLOSE
	const item: ToastItem = {
		id,
		type: options.type ?? 'info',
		message,
		description: options.description,
		action: options.action,
		link: options.link,
		autoClose: auto,
		createdAt: Date.now(),
		hardColor: options.hardColor,
	}

	const existingIdx = items.value.findIndex((t) => t.id === id)
	if (existingIdx >= 0) {
		clearTimer(id)
		items.value.splice(existingIdx, 1, item)
	} else {
		items.value.push(item)
		if (items.value.length > MAX_TOASTS) {
			const overflow = items.value.shift()
			if (overflow) clearTimer(overflow.id)
		}
	}

	if (auto !== false && auto > 0) scheduleRemoval(id, auto)
	return id
}

export const removeToast = (id: string) => {
	clearTimer(id)
	const idx = items.value.findIndex((t) => t.id === id)
	if (idx >= 0) items.value.splice(idx, 1)
}

export const pauseToast = (id: string) => {
	const t = timers.get(id)
	if (!t || t.timeoutId === null) return
	const elapsed = Date.now() - t.startedAt
	const remaining = Math.max(0, t.remainingMs - elapsed)
	clearTimeout(t.timeoutId)
	timers.set(id, { remainingMs: remaining, startedAt: Date.now(), timeoutId: null })
}

export const resumeToast = (id: string) => {
	const t = timers.get(id)
	if (!t || t.timeoutId !== null) return
	if (t.remainingMs <= 0) {
		removeToast(id)
		return
	}
	const handle = setTimeout(() => removeToast(id), t.remainingMs)
	timers.set(id, { remainingMs: t.remainingMs, startedAt: Date.now(), timeoutId: handle })
}

export const clearAllToasts = () => {
	timers.forEach((t) => {
		if (t.timeoutId) clearTimeout(t.timeoutId)
	})
	timers.clear()
	items.value = []
}

type ConvenienceOptions = Omit<ToastOptions, 'type'>

export const toast = {
	success: (message: string, opts?: ConvenienceOptions) => addToast(message, { ...opts, type: 'success' }),
	error: (message: string, opts?: ConvenienceOptions) => addToast(message, { ...opts, type: 'error' }),
	warning: (message: string, opts?: ConvenienceOptions) => addToast(message, { ...opts, type: 'warning' }),
	info: (message: string, opts?: ConvenienceOptions) => addToast(message, { ...opts, type: 'info' }),
	show: (message: string, opts?: ToastOptions) => addToast(message, opts),
	dismiss: (id: string) => removeToast(id),
	clear: () => clearAllToasts(),
}
